import React, { useState } from 'lib/react';
import {
  EVGrid,
  EVRadio,
  EVCheckbox,
  EVButton,
  EVExpandMore,
  EVExpandLess,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { useSelector, useDispatch } from 'lib/redux';
import { expandPanel } from 'features/finalizeOrder/FinalizeOrder.actions';
import { UPDATE_SUBSTITUTION_CUSTOMIZATION_VALUES, UPDATE_INCLUDE_CUSTOM_COVER } from 'constants/ActionTypes';
import clsx from 'lib/clsx';
import { getAdditionalReportFormatOptions } from 'features/finalizeOrder/components/substitutionsAndCustomization/SubstitutionsAndCustomization.constants';
import CommonCustomCover from 'commonComponents/customCover/index';
import Typography from 'commonComponents/typography';
import { PRODUCT_FAMILY_ID_EVCORE } from 'constants/AppConstants';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import SubstitutionsAndCustomizationStyles from './SubstitutionsAndCustomizationStyles';
import FinalizeOrderExpansionPanel from '../FinalizeOrderExpansionPanel.component';
import { PRODUCT_SUBSTITUTIONS_OPTIONS } from './SubstitutionsAndCustomization.constants';
import getSubstitutionQuestions from './SubstitutionsAndCustomization.util';

function SubstitutionsAndCustomization() {
  const classes = SubstitutionsAndCustomizationStyles();
  const isExpanded = useSelector(({ finalizeOrder }) => finalizeOrder.isExpanded);
  const { t } = useTranslation();
  const getPanelHeader = (header) => (<EVGrid>{header}</EVGrid>);
  const dispatch = useDispatch();
  const substitutionsAndCustomizationValues = useSelector(({ finalizeOrder }) => (finalizeOrder.substitutionsAndCustomizationValues));
  const orderPreferences = useSelector(({ userData }) => (userData.orderPreferences));
  const selectedProduct = useSelector(({ placeOrder }) => (placeOrder.productSets[0]));
  const controlStates = useSelector(({ userData }) => (userData.controlStates));
  // Hide Additional Report Formats for EVCore product (productFamilyId=7)
  const productFamilyId = useSelector(({ userData }) => userData?.initParameters?.productFamilyId);
  const [includeCustomCoverCB, setincludeCustomCoverCB] = useState(
    getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, 'reportOptionsCustomCover').value,
  );
  const [showCustomCover, setShowCustomCover] = useState(false);

  const handleChange = (propertyName) => (e) => {
    dispatch({
      type: UPDATE_SUBSTITUTION_CUSTOMIZATION_VALUES, propertyName, propertyKey: 'value', propertyValue: Number(e.target.value),
    });
  };
  const handleincludeCustomCoverChange = (e) => {
    setincludeCustomCoverCB(e.target.checked);
    dispatch({ type: UPDATE_INCLUDE_CUSTOM_COVER, includeCustomCover: e.target.checked });
    if (includeCustomCoverCB) setShowCustomCover(false);
  };
  const handlePropertyChange = (propertyName, key, value) => (dispatch({
    type: UPDATE_SUBSTITUTION_CUSTOMIZATION_VALUES, propertyName, propertyKey: key, propertyValue: value,
  }));

  const getOptionIcon = (option, propertyName, isChecked) => {
    const disabled = !getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, propertyName).canEdit;
    return (
      <EVGrid className={clsx(classes.optionLabel, option, isChecked && 'selected', disabled && 'disabled')}>
        {t(`common_${option}`)}
      </EVGrid>
    );
  };

  const hideAdditionalFormat = controlStates?.additionalFormats?.hidden || productFamilyId === PRODUCT_FAMILY_ID_EVCORE;
  const hideCustomCover = controlStates?.reportOptions?.hidden;
  const questions = getSubstitutionQuestions(selectedProduct?.primaryProductId, selectedProduct?.addOnProductIds?.[0], productFamilyId);
  const questionRadio = () => (
    <>
      <Typography variant="h3-600" fontColor="charcoal-800">
        {t('finalize_order__substitutions_and_customization_heading')}
      </Typography>
      { questions.map((propertyName) => (
        getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, propertyName).visible && (
          <EVGrid
            key={propertyName} // #genai - Property names are stable question identities.
            container
            direction="column"
            style={{ gap: '10px' }}
          >
            <Typography variant="body-400" fontColor="charcoal-800">
              {t(`order_preferences__product_substitutions_question_${propertyName}`)}
            </Typography>
            <EVGrid className={classes.optionsContainer}>
              {Object.entries(PRODUCT_SUBSTITUTIONS_OPTIONS).map(([optionKey, optionValue]) => (
                <EVGrid direction="row" style={{ display: 'flex', gap: '6px' }} key={optionKey}>
                  <EVRadio
                    key={optionKey}
                    id={`${optionKey.toLowerCase()}-${propertyName}`}
                    value={optionValue}
                    name={propertyName}
                    checked={getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, propertyName).value === optionValue}
                    onChange={handleChange(propertyName)}
                    color="primary"
                    disabled={!getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, propertyName).canEdit}
                    className={classes.radioInput}
                    icon={getOptionIcon(optionKey.toLowerCase(), propertyName, false)}
                    checkedIcon={getOptionIcon(optionKey.toLowerCase(), propertyName, true)}
                  />
                </EVGrid>
              ))}
            </EVGrid>
          </EVGrid>
        )
      ))}
    </>
  );

  const addonsReportFormatFieldProps = getAdditionalReportFormatOptions();
  const handleAdditionalFormatChange = (propertyName) => ({ target: { checked } }) => {
    dispatch({
      type: UPDATE_SUBSTITUTION_CUSTOMIZATION_VALUES, propertyName, propertyKey: 'value', propertyValue: checked,
    });
  };

  const additionalFormats = () => (
    <EVGrid container direction="column" style={{ gap: '10px' }}>
      <Typography variant="subtitle-400" fontColor="charcoal-800">
        {t('finalize_order__substitutions_and_customization_additional_reports_text')}
      </Typography>
      <EVGrid className={classes.addonsElementsCB2}>
        {addonsReportFormatFieldProps.map(({
          text, id, key,
        }) => (
          <EVGrid className={classes.addonsElementsCB} key={id}>
            <EVCheckbox
              id={id}
              key={key}
              checked={getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, key).value}
              disabled={!getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, key).canEdit}
              onChange={handleAdditionalFormatChange(key)}
              color="primary"
              className={classes.checkChanges}
            />
            <label className={classes.substitutionsAndCustomizationAdditionalFormat} htmlFor={id}>{t(text)}</label>
          </EVGrid>
        ))}
      </EVGrid>
    </EVGrid>
  );

  const addons = () => (
    <EVGrid>
      <EVGrid className={classes.customCoverCB}>
        <EVCheckbox
          id="property-owner-report-CB"
          name="property-owner-report-CB"
          checked={Boolean(getPropertyValues(orderPreferences, substitutionsAndCustomizationValues, 'reportOptionsDeliverablePropertyOwner').value)}
          onChange={handleAdditionalFormatChange('reportOptionsDeliverablePropertyOwner')}
          color="primary"
          className={classes.checkChanges}
        />
        <label className={classes.substitutionsAndCustomizationAdditionalFormat} htmlFor="property-owner-report-CB">
          {t('order_preferences__property_owner_report')}
        </label>
      </EVGrid>
      { !hideCustomCover && (
      <EVGrid className={classes.customCoverCB}>
        <EVCheckbox
          id="include-custom-cover-page-CB"
          checked={includeCustomCoverCB}
          onChange={handleincludeCustomCoverChange}
          color="primary"
          className={classes.checkChanges}
        />
        <label className={classes.substitutionsAndCustomizationAdditionalFormat} htmlFor="include-custom-cover-page-CB">
          {t('finalize_order__substitutions_and_customization_include_custom_cover_CB')}
        </label>
        {Boolean(includeCustomCoverCB) && (
        <EVButton
          variant="outlined"
          color="primary"
          className={classes.hideOptionButton}
          onClick={() => setShowCustomCover(!showCustomCover)}
        >
          <Typography variant="subtitle-400" fontColor="ocean-600">
            {t(showCustomCover ? 'common_hide_options' : 'common_show_options')}
          </Typography>
          {showCustomCover ? <EVExpandLess /> : <EVExpandMore />}
        </EVButton>
        )}
      </EVGrid>
      )}
    </EVGrid>
  );

  return (

    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader(t('finalize_order__substitutions_and_customization'))}
      defaultExpanded
      expanded={isExpanded.substitutionsAndCustomization}
      onChange={(_, expanded) => dispatch(expandPanel('substitutionsAndCustomization', expanded))}
    >
      <EVGrid container direction="column" style={{ gap: '24px' }}>
        {questions.length > 0 && questionRadio()}
        { !hideAdditionalFormat && (
        <EVGrid container direction="column" style={{ gap: '10px' }}>
          <Typography variant="h3-600" fontColor="charcoal-800">
            {t('order_preferences__product_addons_additional_report')}
          </Typography>
          {additionalFormats()}
        </EVGrid>
        )}
        <EVGrid container direction="column" style={{ gap: '10px' }}>
          <Typography variant="h3-600" fontColor="charcoal-800">
            {t('finalize_order__substitutions_and_customization_AddOns')}
          </Typography>
          {addons()}
        </EVGrid>
        {showCustomCover && (
        <CommonCustomCover
          orderPreferences={orderPreferences}
          inputFieldValues={substitutionsAndCustomizationValues}
          handlePropertyChange={handlePropertyChange}
        />
        )}
      </EVGrid>
    </FinalizeOrderExpansionPanel>
  );
}

export default SubstitutionsAndCustomization;
